/**
 * Helper functions for creating sidebar items and data transformations
 * Reduces code duplication and provides reusable patterns
 */

import { ENDPOINTS, DATE_FORMATS, DEFAULT_VALUES, LEGEND_POSITIONS } from './constants.js';
import dayjs from 'dayjs';

/**
 * Combines title and subTitle into a single widget title
 * @param {string} title - Main title
 * @param {string} [subTitle] - Optional subtitle
 * @returns {string} Combined title
 */
const combineTitle = (title, subTitle) => {
    if (!subTitle) return title;
    return `${title} ${subTitle}`;
};

/**
 * Transforms cloud cost data details into a standardized format
 * Matches AnalyzerView's data processing: extracts time, grouping, measure from API response
 * @param {Object} data - API response data
 * @param {string} groupingField - Field name to use for grouping
 * @param {string} dateFormat - Date format string (default: "MMM YYYY")
 * @returns {Object} Transformed data with mapped details and dateFormat
 */
export const transformCloudCostDetails = (data, groupingField, dateFormat = DATE_FORMATS.MONTH) => {
    const details = data?.CloudCost?.details || data?.details || [];
    const mapped = details.map((d) => {
        // Extract time from groupings array (index 0) - matches AnalyzerView: d.time = d.groupings[0].value
        const time = d?.groupings?.[0]?.value || d.time || d.date;
        
        // Extract grouping from groupings array (index 1) - matches AnalyzerView: d.grouping = d.groupings[1].value
        let grouping = d?.groupings?.[1]?.value;
        
        // Fallback: try the groupingField on the object directly
        if (!grouping) {
            grouping = d[groupingField];
        }
        
        // Handle project grouping - API returns project_name but groupingField might be "project"
        if (!grouping && groupingField === 'project') {
            grouping = d.project_name || d.project;
        }
        
        // Fallback to other common grouping fields if still not found
        if (!grouping) {
            grouping = d.organization || d.organisation_name || d.name;
        }
        
        // Extract measure from measures array (index 0) - matches AnalyzerView: d.measure = d.measures[0].value
        const measure = d?.measures?.[0]?.value ?? d.measure ?? d.cost ?? d.value ?? 0;
        
        return {
            time,
            grouping: grouping || 'Total',
            measure
        };
    });
    return { data: mapped, dateFormat };
};

/**
 * Transforms forecast data into a standardized format
 * @param {Object} data - API response data
 * @param {string} groupingField - Field name to use for grouping
 * @param {string} dateFormat - Date format string
 * @returns {Object} Transformed data with mapped details and dateFormat
 */
export const transformForecastData = (data, groupingField, dateFormat = DATE_FORMATS.DAY) => {
    const details = data?.timeseries || data?.details || [];
    const mapped = details.map((d) => ({
        time: d.time || d?.groupings?.[0]?.value,
        grouping: d[groupingField] || d?.groupings?.[1]?.value,
        measure: (d.forecast ?? d.measure ?? d.value ?? 0)
    }));
    return {
        data: mapped,
        dateFormat
    };
};

/**
 * Transforms generic API data for tables
 * @param {Object} data - API response data
 * @param {string} grouping - Grouping field name
 * @param {Array<string>} dataPaths - Array of possible paths to data (e.g., ['items', 'details'])
 * @returns {Object} Transformed data with data array and grouping
 */
export const transformTableData = (data, grouping, dataPaths = ['items', 'details']) => {
    let resultData = [];
    for (const path of dataPaths) {
        if (data?.[path]) {
            resultData = Array.isArray(data[path]) ? data[path] : [];
            break;
        }
        // Handle nested paths like CloudCost.details
        if (path.includes('.')) {
            const parts = path.split('.');
            let nested = data;
            for (const part of parts) {
                nested = nested?.[part];
            }
            if (nested) {
                resultData = Array.isArray(nested) ? nested : [];
                break;
            }
        }
    }
    return {
        data: resultData,
        grouping
    };
};

/**
 * Creates a standardized API data source configuration
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method (default: "POST")
 * @param {Object} body - Request body
 * @param {Function} transform - Transform function for response data
 * @returns {Object} Data source configuration
 */
export const createAPIDataSource = (endpoint, method = "POST", body = {}, transform) => ({
    type: "api",
    endpoint,
    method,
    body,
    transform
});

/**
 * Creates a static data source configuration
 * @param {Object} data - Static data object
 * @returns {Object} Data source configuration
 */
export const createStaticDataSource = (data) => ({
    type: "static",
    data
});

/**
 * Creates a cost bar chart sidebar item
 * @param {Object} config - Configuration object
 * @param {string} config.title - Widget title
 * @param {string} config.subTitle - Widget subtitle
 * @param {string} config.grouping - Grouping field for API
 * @param {string} [config.groupingField] - Alternative field name for grouping in transform
 * @param {string} [config.tooltip] - Tooltip text (defaults to title)
 * @param {string} [config.tooltipDescription] - Tooltip description
 * @param {Object} [config.defaultSize] - Default size configuration
 * @param {Object} [config.extraConfig] - Additional widget config properties
 * @returns {Object} Sidebar item configuration
 */
export const createCostBarChart = ({
    title,
    subTitle,
    grouping,
    groupingField,
    tooltip,
    tooltipDescription,
    defaultSize,
    extraConfig = {}
}) => ({
    iconName: "chart-bar",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || `Show costs grouped by ${grouping}`,
    widgetType: "barchart",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: combineTitle(title, subTitle),
        valueName: DEFAULT_VALUES.VALUE_NAME,
        dateFormat: DATE_FORMATS.MONTH,
        showButtons: true,
        legendPosition: LEGEND_POSITIONS.RIGHT,
        ...extraConfig,
        dataSource: createAPIDataSource(
            ENDPOINTS.CLOUD_COST,
            "POST",
            { grouping: [grouping] },
            (data) => transformCloudCostDetails(data, groupingField || grouping, DATE_FORMATS.MONTH)
        )
    }
});

/**
 * Creates a cost area chart sidebar item
 * @param {Object} config - Configuration object
 * @returns {Object} Sidebar item configuration
 */
export const createCostAreaChart = ({
    title,
    subTitle,
    grouping,
    groupingField,
    tooltip,
    tooltipDescription,
    defaultSize,
    extraConfig = {}
}) => ({
    iconName: "chart-area",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || `Show costs by ${grouping} over time`,
    widgetType: "barchart",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: combineTitle(title, subTitle),
        valueName: DEFAULT_VALUES.VALUE_NAME,
        dateFormat: DATE_FORMATS.MONTH,
        showButtons: true,
        legendPosition: LEGEND_POSITIONS.RIGHT,
        ...extraConfig,
        dataSource: createAPIDataSource(
            ENDPOINTS.CLOUD_COST,
            "POST",
            { grouping: [grouping], group_by_date: true, date_granularity: "month" },
            (data) => transformCloudCostDetails(data, groupingField || grouping, DATE_FORMATS.MONTH)
        )
    }
});

/**
 * Creates a cost donut chart sidebar item
 * @param {Object} config - Configuration object
 * @returns {Object} Sidebar item configuration
 */
export const createCostDonutChart = ({
    title,
    subTitle,
    grouping,
    tooltip,
    tooltipDescription,
    defaultSize
}) => ({
    iconName: "chart-pie",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || `Show costs grouped by ${grouping} as a donut chart`,
    widgetType: "donutchart",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: combineTitle(title, subTitle),
        nameKey: DEFAULT_VALUES.NAME_KEY,
        valueKey: DEFAULT_VALUES.VALUE_KEY,
        format: DEFAULT_VALUES.FORMAT_CURRENCY,
        dataSource: createAPIDataSource(
            ENDPOINTS.CLOUD_COST,
            "POST",
            { grouping: [grouping] },
            (data) => ({ data: data.details || [] })
        )
    }
});

/**
 * Creates a cost table sidebar item
 * @param {Object} config - Configuration object
 * @returns {Object} Sidebar item configuration
 */
export const createCostTable = ({
    title,
    subTitle,
    grouping,
    valueName,
    tooltip,
    tooltipDescription,
    defaultSize,
    extraConfig = {}
}) => ({
    iconName: "table",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || `Tabular view of costs by ${grouping}`,
    widgetType: "table",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: combineTitle(title, subTitle),
        grouping,
        valueName: valueName || "cost",
        dataSource: createAPIDataSource(
            ENDPOINTS.CLOUD_COST_TABLE,
            "POST",
            extraConfig.body || { grouping: [grouping] },
            (data) => transformTableData(data, grouping, ['breakdown', 'details'])
        )
    }
});

/**
 * Creates a KPI sidebar item
 * @param {Object} config - Configuration object
 * @returns {Object} Sidebar item configuration
 */
export const createKPI = ({
    title,
    subTitle,
    widgetTitle,
    tooltip,
    tooltipDescription,
    format,
    backgroundColor,
    dataSource,
    defaultSize
}) => ({
    iconName: "gauge",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || title,
    widgetType: "kpi",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: widgetTitle || combineTitle(title, subTitle),
        format: format || DEFAULT_VALUES.FORMAT_CURRENCY,
        backgroundColor: backgroundColor || "lightblue10",
        style: "small",
        showChange: false,
        dataSource
    }
});

/**
 * Creates a forecast area chart sidebar item
 * @param {Object} config - Configuration object
 * @returns {Object} Sidebar item configuration
 */
export const createForecastAreaChart = ({
    title,
    subTitle,
    grouping,
    groupingField,
    tooltip,
    tooltipDescription,
    defaultSize
}) => ({
    iconName: "chart-line",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || `Forecasted costs over time by ${grouping}`,
    widgetType: "areachart",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: combineTitle(title, subTitle),
        dateFormat: DATE_FORMATS.DAY,
        stacked: false,
        showButtons: true,
        legendPosition: LEGEND_POSITIONS.BOTTOM,
        dataSource: createAPIDataSource(
            ENDPOINTS.FORECAST,
            "POST",
            { 
                grouping: [grouping], 
                group_by_date: true
            },
            (data) => transformForecastData(data, groupingField || grouping, DATE_FORMATS.DAY)
        )
    }
});

/**
 * Transforms forecast data for ForecastView widget
 * Converts API response to format expected by ForecastView component
 * @param {Object} data - API response data
 * @param {string} groupingField - Field name to use for grouping
 * @returns {Object} Transformed data with CloudCost structure
 */
const transformForecastViewData = (data, groupingField) => {
    // Handle the forecast API response format
    // The API returns data in a format that needs to be transformed to match ForecastView expectations
    const details = data?.CloudCost?.details || data?.details || [];
    
    const transformedDetails = details.map((d) => {
        // Handle both API response format and already transformed format
        if (d.groupings && Array.isArray(d.groupings)) {
            return {
                time: d.groupings[0]?.value || d.time,
                grouping: d.groupings[1]?.value || d[groupingField] || d.grouping || 'Total',
                measure: d.measures?.[0]?.value ?? d.measure ?? 0,
                forecast: d.measures?.[1]?.value ?? d.forecast ?? 0
            };
        }
        // Already transformed format
        return {
            time: d.time,
            grouping: d[groupingField] || d.grouping || 'Total',
            measure: d.measure ?? 0,
            forecast: d.forecast ?? 0
        };
    });

    return {
        CloudCost: {
            details: transformedDetails,
            grouped_totals: data?.CloudCost?.grouped_totals || data?.grouped_totals || []
        }
    };
};

/**
 * Transforms forecast data for KPI widget
 * Returns data in the same format as transformForecastViewData for consistency
 * @param {Object} data - API response data
 * @param {string} groupingField - Field name to use for grouping
 * @returns {Object} Transformed data with CloudCost structure (same as ForecastTableWidget)
 */
export const transformForecastKPIData = (data, groupingField) => {
    // Use the same transform as ForecastTableWidget for consistency
    return transformForecastViewData(data, groupingField);
};

/**
 * Calculates current month forecasted costs from forecast data
 * Uses the same logic as ForecastTableWidget for consistency
 * @param {Object} data - Transformed forecast data with CloudCost.details structure
 * @param {string} [selectedSeries] - Optional selected series/resource to filter by
 * @returns {number} Total forecasted cost for current month
 */
export const calculateForecastKPICurrentValue = (data, selectedSeries = null) => {
    const details = data?.CloudCost?.details || [];
    
    if (!details || details.length === 0) {
        return 0;
    }

    const now = dayjs();
    const currentMonthStart = now.startOf('month');
    const currentMonth = currentMonthStart.format('YYYY-MM');

    // Group by resource and calculate current month costs (same logic as ForecastTableWidget)
    const resourceMap = new Map();
    
    details.forEach((d) => {
        // Handle both API response format and already transformed format (same as ForecastTableWidget)
        let time, grouping, measure, forecast;
        
        if (d.groupings && Array.isArray(d.groupings)) {
            time = d.groupings[0]?.value || d.time;
            grouping = d.groupings[1]?.value || d.grouping || 'Total';
            measure = d.measures?.[0]?.value ?? d.measure ?? 0;
            forecast = d.measures?.[1]?.value ?? d.forecast ?? 0;
        } else {
            time = d.time;
            grouping = d.grouping || 'Total';
            measure = d.measure ?? 0;
            forecast = d.forecast ?? 0;
        }
        
        // Filter by selected series if provided
        if (selectedSeries && grouping !== selectedSeries) {
            return;
        }
        
        const itemDate = dayjs(time);
        if (!itemDate.isValid()) {
            return;
        }
        
        const itemMonth = itemDate.format('YYYY-MM');
        
        if (itemMonth === currentMonth) {
            if (!resourceMap.has(grouping)) {
                resourceMap.set(grouping, {
                    currentMonthActual: 0,
                    currentMonthForecast: 0
                });
            }
            
            const resource = resourceMap.get(grouping);
            // Sum actual costs (measure) and forecast costs separately
            resource.currentMonthActual += (measure || 0);
            resource.currentMonthForecast += (forecast || 0);
        }
    });

    // Calculate total: sum of all resources' (actual + forecast) for current month
    let currentMonthTotal = 0;
    resourceMap.forEach((resource) => {
        currentMonthTotal += resource.currentMonthActual + resource.currentMonthForecast;
    });

    return currentMonthTotal;
};

/**
 * Creates a forecast chart sidebar item (chart view only)
 * Note: For showing forecast by resource for all resources, we use the forecast endpoint
 * with grouping in the body. If the API requires category/productID, those can be added
 * as query parameters via the params field in the dataSource.
 * @param {Object} config - Configuration object
 * @param {string} [config.category] - Category for forecast (e.g., "Resource")
 * @param {string} [config.productID] - Product ID for forecast (optional, for all resources leave empty)
 * @returns {Object} Sidebar item configuration
 */
export const createForecastChart = ({
    title,
    subTitle,
    grouping,
    groupingField,
    tooltip,
    tooltipDescription,
    defaultSize,
    category,
    productID
}) => {
    const dataSource = createAPIDataSource(
        ENDPOINTS.FORECAST,
        "POST",
        { grouping: [grouping], group_by_date: true },
        (data) => transformForecastViewData(data, groupingField || grouping)
    );
    
    // Initialize dataSource params with forecastType set to "cost" by default
    dataSource.params = {
        forecastType: "cost"
    };
    
    // Add query parameters if category/productID are provided
    if (category || productID) {
        if (category) dataSource.params.category = category;
        if (productID) dataSource.params.productID = productID;
    }
    
    return {
        iconName: "chart-area",
        title,
        subTitle,
        tooltip: tooltip || title,
        tooltipDescription: tooltipDescription || `Forecasted costs over time by ${grouping} in a chart view`,
        widgetType: "forecastchart",
        ...(defaultSize && { defaultSize }),
        widgetConfig: {
            title: combineTitle(title, subTitle),
            dateFormat: DATE_FORMATS.DAY,
            dateGrouping: { label: "Day", value: "MMM D" },
            showButtons: true,
            stacked: true,
            valueName: "Total Costs",
            forecastType: "cost",
            dataSource
        }
    };
};

/**
 * Creates a forecast table sidebar item (table view only)
 * Note: For showing forecast by resource for all resources, we use the forecast endpoint
 * with grouping in the body. If the API requires category/productID, those can be added
 * as query parameters via the params field in the dataSource.
 * @param {Object} config - Configuration object
 * @param {string} [config.category] - Category for forecast (e.g., "Resource")
 * @param {string} [config.productID] - Product ID for forecast (optional, for all resources leave empty)
 * @returns {Object} Sidebar item configuration
 */
export const createForecastTable = ({
    title,
    subTitle,
    grouping,
    groupingField,
    tooltip,
    tooltipDescription,
    defaultSize,
    category,
    productID
}) => {
    const dataSource = createAPIDataSource(
        ENDPOINTS.FORECAST,
        "POST",
        { 
            grouping: [grouping], 
            group_by_date: true,
            useDynamicDates: true // Always use dynamic date range: start of previous month to end of next month
        },
        (data) => transformForecastViewData(data, groupingField || grouping)
    );

    // Initialize dataSource params with forecastType set to "cost" by default
    dataSource.params = {
        forecastType: "cost"
    };
    
    // Add query parameters if category/productID are provided
    if (category || productID) {
        dataSource.params = {};
        if (category) dataSource.params.category = category;
        if (productID) dataSource.params.productID = productID;
    }
    
    return {
        iconName: "table",
        title,
        subTitle,
        tooltip: tooltip || title,
        tooltipDescription: tooltipDescription || `Forecasted costs over time by ${grouping} in a table view`,
        widgetType: "forecasttable",
        ...(defaultSize && { defaultSize }),
        widgetConfig: {
            title: combineTitle(title, subTitle),
            dateFormat: DATE_FORMATS.DAY,
            dateGrouping: { label: "Day", value: "MMM D" },
            grouping: grouping || "Resource",
            dataSource
        }
    };
};

/**
 * Creates a resource table sidebar item
 * @param {Object} config - Configuration object
 * @param {string} [config.method="POST"] - HTTP method
 * @param {Function} [config.customTransform] - Custom transform function (overrides default)
 * @returns {Object} Sidebar item configuration
 */
export const createResourceTable = ({
    title,
    subTitle,
    endpoint,
    grouping,
    valueName,
    tooltip,
    tooltipDescription,
    defaultSize,
    body = {},
    method = "POST",
    customTransform
}) => ({
    iconName: "table",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || title,
    widgetType: "table",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: combineTitle(title, subTitle),
        grouping,
        valueName,
        dataSource: {
            type: "api",
            endpoint,
            method,
            ...(body && Object.keys(body).length > 0 && { body }),
            transform: customTransform || ((data) => transformTableData(data, grouping, ['items', 'details']))
        }
    }
});

/**
 * Creates an area chart sidebar item for resources
 * @param {Object} config - Configuration object
 * @returns {Object} Sidebar item configuration
 */
export const createResourceAreaChart = ({
    title,
    subTitle,
    endpoint,
    tooltip,
    tooltipDescription,
    defaultSize,
    body = {}
}) => ({
    iconName: "chart-line",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || title,
    widgetType: "areachart",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: combineTitle(title, subTitle),
        dateFormat: DATE_FORMATS.DAY,
        stacked: false,
        showButtons: true,
        legendPosition: LEGEND_POSITIONS.BOTTOM,
        dataSource: createAPIDataSource(
            endpoint,
            "POST",
            body,
            (data) => ({
                data: data.timeseries || data.details || [],
                dateFormat: DATE_FORMATS.DAY
            })
        )
    }
});

/**
 * Creates a resource changes trend sidebar item (Line Chart)
 * @param {Object} config - Configuration object
 * @param {string} config.title - Widget title
 * @param {string} config.subTitle - Widget subtitle
 * @param {string} [config.widgetTitle] - Custom widget title (overrides combined title)
 * @param {string} config.endpoint - API endpoint
 * @param {string} [config.tooltip] - Tooltip text (defaults to title)
 * @param {string} [config.tooltipDescription] - Tooltip description
 * @param {Object} [config.defaultSize] - Default size configuration
 * @param {Object} [config.body] - Request body for API call
 * @returns {Object} Sidebar item configuration
 */
export const createResourceChangesTrend = ({
    title,
    subTitle,
    widgetTitle,
    endpoint,
    tooltip,
    tooltipDescription,
    defaultSize,
    body = {}
}) => ({
    iconName: "chart-line",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || title,
    widgetType: "resourcechangestrend",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: widgetTitle || combineTitle(title, subTitle),
        dataSource: createAPIDataSource(
            endpoint,
            "POST",
            body,
            (data) => ({
                ResourceChanges: data.ResourceChanges || data.resourceChanges || []
            })
        )
    }
});

/**
 * Creates a resource changes overview sidebar item
 * Shows aggregated resource changes per resource cluster with icons
 * @param {Object} config - Configuration object
 * @param {string} config.title - Widget title
 * @param {string} config.subTitle - Widget subtitle
 * @param {string} config.endpoint - API endpoint
 * @param {string} [config.tooltip] - Tooltip text (defaults to title)
 * @param {string} [config.tooltipDescription] - Tooltip description
 * @param {Object} [config.defaultSize] - Default size configuration
 * @param {Object} [config.body] - Request body for API call
 * @returns {Object} Sidebar item configuration
 */
export const createResourceChangesOverview = ({
    title,
    subTitle,
    endpoint,
    tooltip,
    tooltipDescription,
    defaultSize,
    body = {}
}) => ({
    iconName: "numbers-overview",
    title,
    subTitle,
    tooltip: tooltip || title,
    tooltipDescription: tooltipDescription || title,
    widgetType: "resourcechangesoverview",
    ...(defaultSize && { defaultSize }),
    widgetConfig: {
        title: combineTitle(title, subTitle),
        hideZeroNetChangeClusters: true,
        dataSource: createAPIDataSource(
            endpoint,
            "POST",
            body,
            (data) => ({
                ResourceChanges: data.ResourceChanges || data.resourceChanges || []
            })
        )
    }
});

/**
 * Creates a resource overview sidebar item
 * Shows total number of resources aggregated per resource cluster with icons
 * Uses the same data as ResourceListWidgetGrouped
 * @param {Object} config - Configuration object
 * @param {string} config.title - Widget title
 * @param {string} config.subTitle - Widget subtitle
 * @param {string} config.endpoint - API endpoint
 * @param {string} [config.tooltip] - Tooltip text (defaults to title)
 * @param {string} [config.tooltipDescription] - Tooltip description
 * @param {Object} [config.defaultSize] - Default size configuration
 * @param {Object} [config.body] - Request body for API call
 * @param {string} [config.organisationID] - Organisation ID query parameter
 * @returns {Object} Sidebar item configuration
 */
export const createResourceOverview = ({
    title,
    subTitle,
    widgetTitle,
    endpoint,
    tooltip,
    tooltipDescription,
    defaultSize,
    body = {},
    organisationID
}) => {
    const dataSource = createAPIDataSource(
        endpoint,
        "POST",
        body,
        (data) => ({
            ResourceList: data.ResourceList || data.data || []
        })
    );
    
    // Add organisationID as query parameter if provided
    if (organisationID !== undefined) {
        dataSource.params = { organisationID: organisationID.toString() };
    }
    
    return {
        iconName: "numbers-overview",
        title,
        subTitle,
        tooltip: tooltip || title,
        tooltipDescription: tooltipDescription || title,
        widgetType: "resourceoverview",
        ...(defaultSize && { defaultSize }),
        widgetConfig: {
            title: widgetTitle || combineTitle(title, subTitle),
            dataSource
        }
    };
};

/**
 * Creates a resource list sidebar item
 * Shows resource list in table format with configurable display levels
 * @param {Object} config - Configuration object
 * @param {string} config.title - Widget title
 * @param {string} config.subTitle - Widget subtitle
 * @param {string} config.endpoint - API endpoint
 * @param {string} [config.tooltip] - Tooltip text (defaults to title)
 * @param {string} [config.tooltipDescription] - Tooltip description
 * @param {Object} [config.defaultSize] - Default size configuration
 * @param {Object} [config.body] - Request body for API call
 * @param {string} [config.organisationID] - Organisation ID query parameter
 * @returns {Object} Sidebar item configuration
 */
export const createResourceList = ({
    title,
    subTitle,
    widgetTitle,
    endpoint,
    tooltip,
    tooltipDescription,
    defaultSize,
    body = {},
    organisationID
}) => {
    const dataSource = createAPIDataSource(
        endpoint,
        "POST",
        body,
        (data) => ({
            ResourceList: data.ResourceList || data.data || []
        })
    );
    
    // Add organisationID as query parameter if provided
    if (organisationID !== undefined) {
        dataSource.params = { organisationID: organisationID.toString() };
    }
    
    return {
        iconName: "table",
        title,
        subTitle,
        tooltip: tooltip || title,
        tooltipDescription: tooltipDescription || title,
        widgetType: "resourcelist",
        ...(defaultSize && { defaultSize }),
        widgetConfig: {
            title: widgetTitle || combineTitle(title, subTitle),
            displayLevel: 'second',
            selectedClusters: ['Compute'],
            dataSource
        }
    };
};


/**
 * Creates a resource list sidebar item
 * Shows resource list in table format with configurable display levels
 * @param {Object} config - Configuration object
 * @param {string} config.title - Widget title
 * @param {string} config.subTitle - Widget subtitle
 * @param {string} config.endpoint - API endpoint
 * @param {string} [config.tooltip] - Tooltip text (defaults to title)
 * @param {string} [config.tooltipDescription] - Tooltip description
 * @param {Object} [config.defaultSize] - Default size configuration
 * @param {Object} [config.body] - Request body for API call
 * @param {string} [config.organisationID] - Organisation ID query parameter
 * @returns {Object} Sidebar item configuration
 */
export const createResourceListGrouped = ({
    title,
    subTitle,
    widgetTitle,
    endpoint,
    tooltip,
    tooltipDescription,
    defaultSize,
    body = {},
    organisationID
}) => {
    const dataSource = createAPIDataSource(
        endpoint,
        "POST",
        body,
        (data) => ({
            ResourceList: data.ResourceList || data.data || []
        })
    );
    
    // Add organisationID as query parameter if provided
    if (organisationID !== undefined) {
        dataSource.params = { organisationID: organisationID.toString() };
    }
    
    return {
        iconName: "table",
        title,
        subTitle,
        tooltip: tooltip || title,
        tooltipDescription: tooltipDescription || title,
        widgetType: "resourcelist",
        ...(defaultSize && { defaultSize }),
        widgetConfig: {
            title: widgetTitle || combineTitle(title, subTitle),
            displayLevel: 'first',
            selectedClusters: [],
            dataSource
        }
    };
};

/**
 * Creates an insight table sidebar item
 * Shows insights in a flat table format with type filtering
 * @param {Object} config - Configuration object
 * @param {string} config.title - Widget title
 * @param {string} config.subTitle - Widget subtitle
 * @param {string} [config.tooltip] - Tooltip text (defaults to title)
 * @param {string} [config.tooltipDescription] - Tooltip description
 * @param {Object} [config.defaultSize] - Default size configuration
 * @param {Array<string>} [config.insightTypes] - Filter by insight types (cost, security, reliability). Empty = all
 * @returns {Object} Sidebar item configuration
 */
export const createInsightTable = ({
    title,
    subTitle,
    widgetTitle,
    tooltip,
    tooltipDescription,
    defaultSize,
    insightTypes = []
}) => {
    const dataSource = createAPIDataSource(
        ENDPOINTS.INSIGHTS,
        "GET",
        {},
        (data) => ({
            Insights: data.Insights || data.insights || []
        })
    );
    
    return {
        iconName: "table",
        title,
        subTitle,
        tooltip: tooltip || title,
        tooltipDescription: tooltipDescription || title,
        widgetType: "insighttable",
        ...(defaultSize && { defaultSize }),
        widgetConfig: {
            title: widgetTitle || (subTitle ? `${title} ${subTitle}` : title),
            showTitle: true,
            insightTypes,
            dataSource
        }
    };
};

/**
 * Creates a cost trend area chart sidebar item
 * Shows cost trend over time grouped by a configurable field (default: cost_resource_type)
 * @param {Object} config - Configuration object
 * @param {string} config.title - Widget title
 * @param {string} config.subTitle - Widget subtitle
 * @param {string} [config.grouping="cost_resource_type"] - Field to group by
 * @param {string} [config.groupingField] - Alternative field name for grouping in transform
 * @param {string} [config.tooltip] - Tooltip text (defaults to title)
 * @param {string} [config.tooltipDescription] - Tooltip description
 * @param {Object} [config.defaultSize] - Default size configuration
 * @param {Object} [config.extraConfig] - Additional widget config properties
 * @returns {Object} Sidebar item configuration
 */
export const createCostTrendChart = ({
    title,
    subTitle,
    widgetTitle,
    grouping = 'cost_resource_type',
    groupingField,
    tooltip,
    tooltipDescription,
    defaultSize,
    extraConfig = {}
}) => {
    // Match AnalyzerView's buildAnalyzerRequest format exactly
    // Use CLOUD_COST_TABLE endpoint (/get-cloud-cost) - same as AnalyzerView
    // No transform - the widget handles data transformation internally
    const dataSource = createAPIDataSource(
        ENDPOINTS.CLOUD_COST_TABLE,
        "POST",
        {
            grouping: [grouping],
            group_by_date: true,
            add_grouped_results: false, // Not needed - widget only uses details
            add_detailed_results: true,
            date_granularity: "Day", // Default to Day, can be overridden
            measures: [
                {
                    "name": "measure", // Match AnalyzerView's measure name
                    "formula": "sum(field.direct_cost)"
                }
            ]
            // Dates are set by the widget from the global filter store
        }
        // No transform function - CostTrendChartWidget handles transformation internally
    );

    return {
        iconName: "chart-area",
        title,
        subTitle,
        tooltip: tooltip || title,
        tooltipDescription: tooltipDescription || `Show cost trend over time grouped by ${grouping}`,
        widgetType: "costtrendchart",
        ...(defaultSize && { defaultSize }),
        widgetConfig: {
            // Use widgetTitle if provided, otherwise construct from title and subTitle
            title: widgetTitle || (subTitle ? `${title} ${subTitle}` : title),
            grouping,
            dateFormat: DATE_FORMATS.DAY,
            dateGrouping: { label: "Day", value: "MMM D" },
            stacked: true,
            showButtons: true,
            legendPosition: LEGEND_POSITIONS.BOTTOM,
            maxGroupings: 10, // Number of items shown in legend, rest grouped as "Other"
            ...extraConfig,
            dataSource
        }
    };
};

/**
 * Validates a sidebar item configuration
 * @param {Object} item - Sidebar item to validate
 * @throws {Error} If required fields are missing
 */
export const validateSidebarItem = (item) => {
    const required = ['iconName', 'title', 'tooltip', 'widgetType', 'widgetConfig'];
    const missing = required.filter(key => !item[key]);
    if (missing.length) {
        throw new Error(`Sidebar item missing required fields: ${missing.join(', ')}`);
    }
    
    // Validate defaultSize if present
    if (item.defaultSize) {
        if (typeof item.defaultSize.defaultW !== 'number' || item.defaultSize.defaultW < 1 || item.defaultSize.defaultW > 12) {
            throw new Error('defaultSize.defaultW must be a number between 1 and 12');
        }
        if (typeof item.defaultSize.defaultH !== 'number' || item.defaultSize.defaultH < 1) {
            throw new Error('defaultSize.defaultH must be a positive number');
        }
    }
};

